import { useState, useContext } from "react";
import { Contract } from "ethers";
import { FormattedMessage } from "react-intl";
import Fade from "react-reveal/Fade";
import { stakeAndBid } from "helpers/bid";
import EthIcon from "assets/eth_black.svg";
import Button from "components/ui/Button";
import { ModalContext } from "context/ModalContext";
import { MODAL_TYPES } from "helpers/constants";
import { addNotify } from "store/notificationMiddleware";

interface RadicalBidContentProps {
  days: number;
  minimumDays: number;
  madPrice: number;
  bid: number;
  bidDayError: boolean;
  bidPriceError: boolean;
  totalBidCollateralError: boolean;
  bidPriceThreshold: number;
  bidDayThreshold: number;
  landData: Land | undefined;
  item: Bid;
  metaverseInfo: any;
  radicalMarketInstance: Contract | null;
  metaverseId: number;
  onCancel: () => void;
  handleBid: (e: any) => void;
  handleBidPriceError: (e: any) => void;
  handleTotalBidCollateralError: (e: any) => void;
  handleDays: (e: any) => void;
  handleBidDayError: (e: any) => void;
  handleLandData?: (e: any) => void;
  handleOpenExtendLeaseModal: (b: boolean) => void;
  [key: string]: any;
}

export function RadicalBidContent(props: RadicalBidContentProps) {
  const {
    onCancel,
    madPrice,
    days: initialDays,
    bid: initialBid,
    minimumDays,
    bidDayError: dayError,
    bidPriceError: priceError,
    totalBidCollateralError: totalBidError,
    bidPriceThreshold,
    bidDayThreshold,
    radicalMarketInstance,
    metaverseId,
    item,
    metaverseInfo,
    itemDetails,
    itemOpenSeaDetails,
    parcelName,
    handleLandData,
    handleBid,
    handleBidPriceError,
    handleTotalBidCollateralError,
    handleDays,
    handleBidDayError,
    landData
  } = props;

  const { showModal } = useContext(ModalContext);

  const [bidDayError, setDayError] = useState(dayError);
  const [bidPriceError, setPriceError] = useState(priceError);
  const [totalBidCollateralError, setTotalBidError] = useState(totalBidError);
  const [bid, setBid] = useState(initialBid || 0);
  const [days, setDays] = useState(initialDays || 0);

  const handleOpenSetBuildModal = () => {
    showModal(MODAL_TYPES.set_builder, {
      radicalMarketInstance,
      metaverseId,
      assetId: item.assetId,
      handleLandData
    });
  };

  const updateBidPerDay = (e: any) => {
    try {
      if (!e.target.value) {
        handleBid(madPrice);
        setBid(madPrice);
      } else {
        const input = parseFloat(e.target.value);
        handleBid(input);
        setBid(input);
        handleBidPriceError(input < bidPriceThreshold);
        setPriceError(input < bidPriceThreshold);
        handleTotalBidCollateralError(false);
        setTotalBidError(false);
      }
    } catch (e) {
      console.log(e);
      handleBid(madPrice);
      setBid(madPrice);
    }
  };

  const updateDays = (e: any) => {
    try {
      if (!e.target.value) {
        handleDays(minimumDays);
        setDays(minimumDays);
      } else {
        const input = parseInt(e.target.value);
        handleDays(input);
        setDays(input);
        handleBidDayError(input < bidDayThreshold);
        setDayError(input < bidDayThreshold);
        handleTotalBidCollateralError(false);
        setTotalBidError(false);
      }
    } catch (e) {
      console.log(e);
      handleDays(minimumDays);
      setDays(minimumDays);
    }
  };

  const submitBid = async () => {
    if (!radicalMarketInstance) {
      return;
    }
    if (!landData || bid * days <= (parseFloat(landData.currentCollateral) / 1e18) * 1.2) {
      handleTotalBidCollateralError(true);
      setTotalBidError(true);
      return;
    } else {
      handleTotalBidCollateralError(false);
      setTotalBidError(false);
    }

    const { success, message } = await stakeAndBid(
      metaverseInfo,
      metaverseId.toString(),
      item.assetId,
      radicalMarketInstance,
      bid,
      days
    );
    if (success) {
      onCancel();
      let parcelN = parcelName;
      if (itemOpenSeaDetails || itemDetails) {
        parcelN =
          itemOpenSeaDetails && itemOpenSeaDetails.name
            ? itemOpenSeaDetails.name
            : itemDetails
            ? itemDetails.name
              ? itemDetails.name
              : `Parcel #${itemDetails.tokenId.substring(0, 12)}`
            : "Loading...";
      }
      setTimeout(() => {
        showModal(MODAL_TYPES.bid_success, {
          landData,
          parcelName: parcelN,
          handleOpenSetBuildModal
        });
      }, 600);
    } else {
      addNotify({
        title: "Transaction Failed",
        message,
        type: "error"
      });
    }
  };

  const renderMainContent = () => {
    return (
      <div className="pt-8 pb-6 md:pb-8 px-6 bg-gray-bg-dark lg:auto modal-container">
        <p className="text-white font-commuter lg:text-3xl text-xl font-semibold mb-8 text-center">
          <FormattedMessage id="Submit your Bid" />
        </p>
        <div className="md:flex items-center lg:text-xl text-12 font-semibold leading-8 pb-6 md:border-b-0 border-white md:mx-0 border-b">
          <div className="flex items-center md:mb-0 mb-6">
            <span className="mr-3">
              <FormattedMessage id="Bid per Day" />:
            </span>
            <img src={EthIcon} alt="eth-icon" className="mr-4 lg:w-7 w-4" />
            <input
              className={`lg:w-28 w-24 py-0 md:py-2 text-center bg-transparent rounded-md lg:text-2xl text-12 px-1 outline-none mr-3 ${
                bidPriceError ? "border-red-200 border-2" : "border-white border border-opacity-30"
              }`}
              type="numeric"
              value={bid}
              min={madPrice}
              onChange={updateBidPerDay}
            />
          </div>
          <div className="flex items-center">
            <span className="text-12 md:mr-3 mr-11">&#10005;</span>
            <span className="mr-3">
              <FormattedMessage id="Rent for" />:
            </span>
            <input
              className={`lg:w-28 w-24 py-0 md:py-2 text-center bg-transparent rounded-md lg:text-2xl text-12 px-1 outline-none mr-3 ${
                bidDayError ? "border-red-200 border-2" : "border-white border border-opacity-30"
              }`}
              type="numeric"
              value={days}
              min={minimumDays ? minimumDays : 0}
              onChange={updateDays}
            />
            <span className="lg:text-xl md:text-12 text-8 md:leading-none md:mt-0 leading-4 mt-auto">
              <FormattedMessage id="Days" />
            </span>
          </div>
        </div>
        <Fade bottom collapse when={bidDayError || bidPriceError || totalBidCollateralError}>
          <div className="text-red-200 flex" style={{ display: "block", minHeight: 24 }}>
            {`${
              bidPriceError
                ? `Bid per day must be larger than ${bidPriceThreshold.toFixed(3)} (80% of the current bid). `
                : ""
            }${
              bidDayError
                ? `Total days must be larger than ${bidDayThreshold.toFixed(3)} (80% of the current rent time). `
                : ""
            }${
              totalBidCollateralError
                ? `Total collateral must be larger than ${
                    landData && parseInt(landData.pendingCollateral)
                      ? ((parseFloat(landData.pendingCollateral) / 1e18) * 1.2).toFixed(3)
                      : landData?.currentCollateral
                      ? ((parseFloat(landData.currentCollateral) / 1e18) * 1.2).toFixed(3)
                      : "0"
                  } (120% of the current collateral).`
                : ""
            }`}
          </div>
        </Fade>
        <div className="flex mb-8 mt-3 md:mr-0 mr-9">
          <span className="lg:text-xl text-12 font-bold mr-8 ml-auto md:block hidden">=</span>
          <span className="md:mr-16 mr-2 my-auto lg:text-xl text-12 md:ml-0 ml-auto">
            {bid * days > 0 ? (bid * days).toFixed(3) : "--"}
          </span>
          <span className="lg:text-xl text-12 font-semibold">
            <FormattedMessage id="Total Staked" />
          </span>
        </div>
        <Button
          text={<FormattedMessage id="Bid Now" />}
          onClick={submitBid}
          className="text-center text-gray-bg-dark bg-yellow-300 hover:bg-yellow-200 font-nexa font-bold text-lg lg:text-xl w-auto ml-auto px-5 py-2.5"
        />
      </div>
    );
  };

  return (
    <div className="transparent">
      <div className="relative border bg-gray-bg-dark border-gray-300 rounded-2xl">{renderMainContent()}</div>
    </div>
  );
}
